import { useState } from 'react';
import InterviewScheduleForm from './InterviewScheduleForm';
import InterviewScheduleCard from './InterviewScheduleCard';
import {
  emptyInterviewSchedule,
  interviewScheduleValid,
  fromDatetimeLocal
} from '../constants/interview';
import { emptyRoundFeedback, ROUND_RECOMMENDATIONS } from '../constants/interviewRounds';
import { RATING_OPTIONS } from '../constants/hiring';
import { submitInterviewRound } from '../utils/interviewRoundUpdate';
import { interviewRoundLabelAt } from '../utils/interviewRoundDisplay';
import { formatInterviewScheduleDisplayLabel } from '../utils/interviewScheduleDisplay';

const ROUND_STATUS_LABELS = {
  pending: 'Pending',
  scheduled: 'Scheduled',
  completed: 'Completed'
};

export default function InterviewRoundsPanel({ app, userRole, onUpdated }) {
  const rounds = app.interviewRounds || [];
  const [feedback, setFeedback] = useState(emptyRoundFeedback());
  const [nextSchedule, setNextSchedule] = useState(emptyInterviewSchedule());
  const [showNextSchedule, setShowNextSchedule] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!rounds.length || app.stage !== 'interview') return null;

  const currentIdx = rounds.findIndex(r => r.roundId === app.currentInterviewRoundId);
  const current = currentIdx >= 0 ? rounds[currentIdx] : rounds.find(r => r.status !== 'completed');
  const hasNext = currentIdx >= 0 && currentIdx < rounds.length - 1;
  const isStaff = userRole === 'admin' || userRole === 'recruiter' || userRole === 'client';
  const canManageRound =
    isStaff && current && current.status === 'scheduled' && app.interviewSchedule?.status !== 'cancelled';

  const feedbackValid = feedback.recommendation && feedback.summary?.trim();
  const nextScheduleValid =
    !showNextSchedule || interviewScheduleValid(nextSchedule, { requireInstructions: true });

  const handleSubmit = async action => {
    setError('');
    if (!feedbackValid) {
      setError('Recommendation and summary are required.');
      return;
    }
    if (action === 'advance' && hasNext && !nextScheduleValid) {
      setError('Complete schedule details for the next round.');
      return;
    }
    setSaving(true);
    try {
      const updated = await submitInterviewRound(app.id, {
        action:
          action === 'reject' || feedback.recommendation === 'no-hire'
            ? 'reject'
            : hasNext && action === 'advance'
              ? 'complete_and_next'
              : 'complete_final',
        feedback,
        interviewSchedule:
          hasNext && action === 'advance' && showNextSchedule
            ? {
                ...nextSchedule,
                scheduledAt: fromDatetimeLocal(
                  nextSchedule.scheduledAtLocal || nextSchedule.scheduledAt
                )
              }
            : undefined,
        rejectionReasonCode: 'other',
        rejectionComment: feedback.summary
      });
      onUpdated(updated);
      setFeedback(emptyRoundFeedback());
      setNextSchedule(emptyInterviewSchedule());
      setShowNextSchedule(false);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save round feedback');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 border border-yellow-200 bg-yellow-50/30 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium text-[hsl(var(--ds-fg))]">Interview rounds</p>
        {app.interviewRoundsProgress && (
          <span className="text-xs text-[hsl(var(--ds-muted-fg))]">
            {app.interviewRoundsProgress} completed
          </span>
        )}
      </div>

      <ol className="space-y-2">
        {rounds.map((round, idx) => {
          const isCurrent = round.roundId === (current?.roundId || app.currentInterviewRoundId);
          return (
            <li
              key={round.roundId}
              className={`border px-3 py-2 text-sm ${
                isCurrent ? 'border-yellow-400 bg-[hsl(var(--ds-surface))]' : 'border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/50'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-[hsl(var(--ds-fg))]">
                  {interviewRoundLabelAt(app, idx)}
                </span>
                <span className="text-xs capitalize text-[hsl(var(--ds-muted-fg))]">
                  {ROUND_STATUS_LABELS[round.status] || round.status}
                </span>
              </div>
              {round.feedback && (
                <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">
                  Feedback: {round.feedback.recommendation?.replace('-', ' ')} —{' '}
                  {round.feedback.summary}
                </p>
              )}
              {round.scheduleDisplay && round.status !== 'pending' && (
                <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">
                  {formatInterviewScheduleDisplayLabel(round.scheduleDisplay)}
                </p>
              )}
            </li>
          );
        })}
      </ol>

      {current && (
        <p className="text-xs text-amber-800">
          Current round:{' '}
          <strong>
            {currentIdx >= 0 ? interviewRoundLabelAt(app, currentIdx) : current.name}
          </strong>
        </p>
      )}

      {app.interviewSchedule && (
        <InterviewScheduleCard app={app} />
      )}

      {canManageRound && (
        <div className="space-y-3 border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-3">
          <p className="text-sm font-medium text-[hsl(var(--ds-fg))]">Round feedback</p>
          <div className="field-with-label">
            <select
              className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
              value={feedback.recommendation}
              onChange={e => setFeedback(f => ({ ...f, recommendation: e.target.value }))}
            >
              <option value="">Select recommendation</option>
              {ROUND_RECOMMENDATIONS.map(r => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <label>Recommendation *</label>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {['technicalRating', 'communicationRating', 'cultureFitRating'].map(field => (
              <div key={field} className="field-with-label">
                <select
                  className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                  value={feedback[field]}
                  onChange={e => setFeedback(f => ({ ...f, [field]: e.target.value }))}
                >
                  {RATING_OPTIONS.map(o => (
                    <option key={o || 'blank'} value={o}>
                      {o || '—'}
                    </option>
                  ))}
                </select>
                <label className="capitalize">{field.replace('Rating', '')}</label>
              </div>
            ))}
          </div>
          <div className="field-with-label">
            <textarea
              className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
              rows={3}
              value={feedback.summary}
              onChange={e => setFeedback(f => ({ ...f, summary: e.target.value }))}
              placeholder=" "
            />
            <label>Summary *</label>
          </div>
          <div className="field-with-label">
            <textarea
              className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
              rows={2}
              value={feedback.strengths}
              onChange={e => setFeedback(f => ({ ...f, strengths: e.target.value }))}
              placeholder=" "
            />
            <label>Strengths</label>
          </div>
          <div className="field-with-label">
            <textarea
              className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
              rows={2}
              value={feedback.concerns}
              onChange={e => setFeedback(f => ({ ...f, concerns: e.target.value }))}
              placeholder=" "
            />
            <label>Concerns</label>
          </div>

          {hasNext && feedback.recommendation === 'hire' && (
            <>
              <label className="flex items-center gap-2 text-sm text-[hsl(var(--ds-fg))]">
                <input
                  type="checkbox"
                  checked={showNextSchedule}
                  onChange={e => setShowNextSchedule(e.target.checked)}
                />
                Schedule next round: {rounds[currentIdx + 1]?.name}
              </label>
              {showNextSchedule && (
                <InterviewScheduleForm
                  value={nextSchedule}
                  onChange={setNextSchedule}
                  clientContacts={app.clientContacts || []}
                  clientAddresses={app.clientAddresses || []}
                />
              )}
            </>
          )}

          {error && <p className="text-xs text-red-600">{error}</p>}

          <div className="flex flex-wrap gap-2">
            {feedback.recommendation === 'no-hire' ? (
              <button
                type="button"
                className="border border-[hsl(var(--ds-danger))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-danger))] px-3 py-2 text-sm disabled:opacity-60"
                disabled={saving || !feedbackValid}
                onClick={() => handleSubmit('reject')}
              >
                {saving ? 'Saving…' : 'Reject candidate'}
              </button>
            ) : hasNext ? (
              <button
                type="button"
                className="border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))] px-3 py-2 text-sm disabled:opacity-60"
                disabled={saving || !feedbackValid || !showNextSchedule || !nextScheduleValid}
                onClick={() => handleSubmit('advance')}
              >
                {saving ? 'Saving…' : 'Complete round & schedule next'}
              </button>
            ) : (
              <button
                type="button"
                className="border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))] px-3 py-2 text-sm disabled:opacity-60"
                disabled={saving || !feedbackValid}
                onClick={() => handleSubmit('complete')}
              >
                {saving ? 'Saving…' : 'Complete final round'}
              </button>
            )}
          </div>
          {hasNext && !showNextSchedule && feedback.recommendation === 'hire' && (
            <p className="text-xs text-amber-700">
              Check “Schedule next round” and enter details to proceed to {rounds[currentIdx + 1]?.name}.
            </p>
          )}
        </div>
      )}

      {app.interviewRoundsComplete && isStaff && (
        <p className="text-xs text-green-800 bg-green-50 border border-green-200 px-2 py-1">
          All interview rounds are complete. You can move this application to Offer from Update stage.
        </p>
      )}
    </div>
  );
}
